import {
  cascadeKeysForEntity,
  cascadeKeysForUi,
  resolveLayered,
  type DisplayContext,
  type SurfaceName,
  type TranslationLayers,
} from '@canvas-logic/clp-localization';

/**
 * ADR-0076: the display surface every lookup in the PDF pipeline resolves
 * under. A project that has authored no `::pdf` copy is unaffected — the
 * cascade probes the whole scope chain under the surface and then the same
 * chain bare, so the base translation is still found.
 *
 * Note this is a different mechanism from the `ui.pdf.*` canonicals below: those
 * are dedicated registry keys for PDF *chrome*, which the platform UI registry
 * can afford because it is closed but extensible. Entity label IDs are derived
 * from the product graph, so no sibling key can be invented for them — hence
 * the axis.
 */
export const PDF_SURFACE: SurfaceName = 'pdf';

/**
 * PDF translation resolution (ADR-0045).
 *
 * The server-rendered PDF historically sourced every string from i18next
 * (bundled `pdf.json`) and from the SDK's default-language `.texts`, so CSV
 * translations imported into `project_translation` never reached it. These
 * helpers resolve a string from the shared translation layers first — applying
 * the same scope cascade the live UI uses — and fall back to the bundled
 * i18next value (UI chrome) or the SDK system value (entity copy) when the
 * project has no translation.
 */

/**
 * Resolve a PDF UI-chrome label (column headers, titles, disclaimer, footer)
 * from a `ui.pdf.*` canonical, honouring composite-scoped overrides. Falls back
 * to `i18nFallback` (the bundled `pdf.json` value) when no project translation
 * exists at any cascade level.
 */
export function resolvePdfUiLabel(
  layers: TranslationLayers,
  uiCanonical: string,
  i18nFallback: string,
  cptKey?: string,
): string {
  const ctx: DisplayContext = cptKey ? { cptKey, surface: PDF_SURFACE } : { surface: PDF_SURFACE };
  return resolveLayered(cascadeKeysForUi(uiCanonical, ctx), layers)?.value ?? i18nFallback;
}

/**
 * Resolve an entity label (composite product name/description, BOM component
 * product name/description) from its canonical through the entity cascade.
 * Falls back to `systemValue` (the SDK default-language text) when the project
 * has no translation — entity copy has no shared i18next catalog.
 */
export function resolvePdfEntityLabel(
  layers: TranslationLayers,
  canonical: string,
  ctx: DisplayContext,
  systemValue: string,
): string {
  // ADR-0076: the surface is forced here rather than left to callers. Every
  // lookup that reaches this function is, by construction, being rendered into
  // the PDF, so making it a parameter would only create a way to get it wrong.
  const pdfCtx: DisplayContext = { ...ctx, surface: PDF_SURFACE };
  return resolveLayered(cascadeKeysForEntity(canonical, pdfCtx), layers)?.value ?? systemValue;
}
